import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';
import { debounce } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';

import { FilterPopover } from './FilterPopover';
import { FormFieldCheckbox } from 'src/common/FormField/FormFieldCheckbox';

export type IdentifiedEntity = { id: string | number };

export interface MultiSelectFilterPopoverProps<
  T extends unknown = IdentifiedEntity,
> extends PropsWithChildren {
  triggerElement: React.ReactNode;
  value?: T[];
  filters: T[];
  open?: boolean;
  searchPlaceholder?: string;
  renderLabel: (value: T) => React.ReactNode;
  onChange: (value: T[]) => void;
  onSearch?: (term: string) => void;
  onCancel?: () => void;
}
export function MultiSelectFilterPopover<T extends IdentifiedEntity>({
  triggerElement,
  value,
  filters,
  open,
  searchPlaceholder = 'Search...',
  renderLabel,
  onChange,
  onSearch,
  onCancel,
  children,
}: MultiSelectFilterPopoverProps<T>) {
  const [selectedFilters, setSelected] = useState([] as T[]);

  const handleSearch = (term: string) => {
    onSearch?.(term);
  };

  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);

  const debounceSearch = debounce(handleSearch, 300);
  return (
    <FilterPopover
      open={open}
      triggerElement={triggerElement}
      onApply={() => {
        onChange(selectedFilters);
        onSearch?.('');
      }}
      onCancel={() => {
        onSearch?.('');
        setSelected(value ?? []);
        onCancel?.();
      }}
      onOpenChange={(isOpen) => {
        // Only treat close transitions as cancels. Firing onCancel on open
        // would reset selection every time the popover is reopened.
        if (!isOpen) {
          setSelected(value ?? []);
          onCancel?.();
        }
      }}
      preFooterContainerElement={
        children ? (
          <div className="border-color-gray-200 border-t py-2">{children}</div>
        ) : null
      }
    >
      <>
        {onSearch && (
          <div className="sticky top-0 z-10 mb-2 bg-white pb-2">
            <SearchTypeahead
              dataTestId="filter-popover-search"
              onChange={debounceSearch}
              placeholder={searchPlaceholder}
            />
          </div>
        )}
        {filters.map((filter) => (
          <div className="my-2" key={filter.id}>
            <FormFieldCheckbox
              type="checkbox"
              label={renderLabel(filter)}
              value={
                !!selectedFilters.find((selected) => selected.id === filter.id)
              }
              onChange={(selected) => {
                let mutSelected = [...selectedFilters];

                if (selected) {
                  mutSelected.push(filter);
                } else {
                  mutSelected = mutSelected.filter((t) => t.id !== filter.id);
                }
                setSelected(mutSelected);
              }}
              inputProps={{
                id: filter.id + '', // Force string for input
              }}
            />
          </div>
        ))}
      </>
    </FilterPopover>
  );
}
